import { useRef, useState } from 'react'
import {
  FileText,
  Image as ImageIcon,
  Download,
  Pencil,
  Trash2,
  Upload,
  Loader2,
  MoreHorizontal,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { showSuccessToast, showErrorToast } from '@/lib/toast'
import { formatDate, formatFileSize } from '@/lib/format'
import { useProjectDocuments } from '@/hooks/useProjectDocuments'
import {
  uploadProjectDocument,
  renameProjectDocument,
  deleteProjectDocument,
  getProjectDocumentDownloadUrl,
  type ProjectDocumentDto,
} from '@/lib/api/v2/projects'

function getUploaderInitials(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName]
    .filter((n): n is string => !!n?.trim())
    .map((n) => n.trim()[0].toUpperCase() + '.')
    .join('') || '—'
}

interface ProjectDocumentsSectionProps {
  projectId: string | undefined
}

/**
 * SOLY-DOC-001 V1 — execution (chantier) documents of a project: list (newest first), upload
 * (image/PDF), rename, soft-delete, open/download via a signed URL. Internal only; access is
 * enforced server-side. No business type in V1 (icon derived from contentType).
 */
export function ProjectDocumentsSection({ projectId }: ProjectDocumentsSectionProps) {
  const { documents, isLoading, error, refetch } = useProjectDocuments(projectId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [renameTarget, setRenameTarget] = useState<ProjectDocumentDto | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProjectDocumentDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  if (!projectId) return null

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files ?? [])
    if (files.length === 0) return
    setIsUploading(true)
    const results = await Promise.allSettled(files.map((f) => uploadProjectDocument(projectId, f)))
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.filter((r) => r.status === 'rejected').length
    if (succeeded > 0) {
      showSuccessToast(succeeded === 1 ? 'Document ajouté' : `${succeeded} documents ajoutés`)
      await refetch()
    }
    if (failed > 0) {
      showErrorToast(failed === 1 ? "Erreur lors de l'envoi d'un document" : `${failed} documents non envoyés`)
    }
    setIsUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDownload = async (doc: ProjectDocumentDto) => {
    try {
      const { url } = await getProjectDocumentDownloadUrl(projectId, doc.id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      showErrorToast("Impossible d'ouvrir le document")
    }
  }

  const submitRename = async () => {
    if (!renameTarget) return
    const name = renameValue.trim()
    if (!name) return
    setIsRenaming(true)
    try {
      await renameProjectDocument(projectId, renameTarget.id, name)
      showSuccessToast('Document renommé')
      setRenameTarget(null)
      await refetch()
    } catch {
      showErrorToast('Erreur lors du renommage')
    } finally {
      setIsRenaming(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteProjectDocument(projectId, deleteTarget.id)
      showSuccessToast('Document supprimé')
      setDeleteTarget(null)
      await refetch()
    } catch {
      showErrorToast('Erreur lors de la suppression')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Card className="py-3">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Documents du chantier</CardTitle>
            <p className="text-xs text-muted-foreground">
              Plans, photos et documents techniques. Le plus récent en premier.
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={handleFileChange}
              data-testid="project-document-file-input"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              data-testid="project-document-upload-btn"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {isUploading ? 'Envoi…' : 'Ajouter'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="py-1.5">Document</TableHead>
                <TableHead className="py-1.5">Taille</TableHead>
                <TableHead className="py-1.5">Par</TableHead>
                <TableHead className="py-1.5">Ajouté le</TableHead>
                <TableHead className="py-1.5 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-destructive">
                    Impossible de charger les documents.
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    Chargement…
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    Aucun document — ajoutez le premier plan ou la première photo.
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="py-1.5 font-medium">
                      <span className="flex items-center gap-2">
                        {doc.contentType.startsWith('image/') ? (
                          <ImageIcon className="h-4 w-4 text-slate-500" />
                        ) : (
                          <FileText className="h-4 w-4 text-red-500" />
                        )}
                        <span className="truncate">{doc.displayName}</span>
                      </span>
                    </TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">{formatFileSize(doc.size)}</TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">
                      {getUploaderInitials(doc.uploaderFirstName, doc.uploaderLastName)}
                    </TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">
                      {formatDate(doc.createdAt, 'd MMM yyyy')}
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid="project-document-actions-btn"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDownload(doc)}
                            data-testid="project-document-download-item"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Ouvrir / Télécharger
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setRenameTarget(doc)
                              setRenameValue(doc.displayName)
                            }}
                            data-testid="project-document-rename-item"
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Renommer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(doc)}
                            className="text-destructive"
                            data-testid="project-document-delete-item"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer le document</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="project-document-rename-input">Nom</Label>
            <Input
              id="project-document-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitRename()
              }}
              data-testid="project-document-rename-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameTarget(null)}
              data-testid="project-document-rename-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={submitRename}
              disabled={isRenaming || renameValue.trim().length === 0}
              data-testid="project-document-rename-save-btn"
            >
              {isRenaming ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              «&nbsp;{deleteTarget?.displayName}&nbsp;» sera retiré du chantier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-document-delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              data-testid="project-document-delete-confirm-btn"
            >
              {isDeleting ? 'Suppression…' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
